import { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { User } from "firebase/auth";
import { message } from "antd";
import { FirebaseContext } from "../context/FirebaseContext";
import {
  ProjectService,
  Project,
  ProjectInput,
} from "../services/ProjectService";

export const useProjects = (user: User | null) => {
  const firebase = useContext(FirebaseContext);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  const projectService = useMemo(
    () => new ProjectService(firebase.db, user),
    [firebase.db, user]
  );

  // Cargar proyectos del usuario
  const loadProjects = useCallback(async () => {
    if (!user) {
      setProjects([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const userProjects = await projectService.getUserProjects();
      setProjects(userProjects);
    } catch (error) {
      console.error("Error loading projects:", error);
      message.error("Error al cargar los proyectos");
    } finally {
      setLoading(false);
    }
  }, [user, projectService]);

  // Crear nuevo proyecto
  const createProject = useCallback(
    async (projectData: ProjectInput): Promise<Project | null> => {
      if (!user) {
        message.error("Debes estar autenticado para crear un proyecto");
        return null;
      }

      try {
        setCreating(true);
        const newProject = await projectService.createProject(projectData);
        setProjects((prev) => [newProject, ...prev]);
        message.success("Proyecto creado exitosamente");
        return newProject;
      } catch (error) {
        console.error("Error creating project:", error);
        message.error("Error al crear el proyecto");
        return null;
      } finally {
        setCreating(false);
      }
    },
    [user, projectService]
  );

  // Eliminar proyecto
  const deleteProject = useCallback(
    async (projectId: string): Promise<boolean> => {
      try {
        setDeleting(projectId);
        await projectService.deleteProject(projectId);
        setProjects((prev) => prev.filter((p) => p.id !== projectId));
        message.success("Proyecto eliminado exitosamente");
        return true;
      } catch (error) {
        console.error("Error deleting project:", error);
        message.error("Error al eliminar el proyecto");
        return false;
      } finally {
        setDeleting(null);
      }
    },
    [projectService]
  );

  // Obtener un proyecto específico
  const getProject = useCallback(
    async (projectId: string): Promise<Project | null> => {
      try {
        const project = await projectService.getProject(projectId);
        return project;
      } catch (error) {
        console.error("Error getting project:", error);
        message.error("Error al acceder al proyecto");
        return null;
      }
    },
    [projectService]
  );

  // Cargar proyectos cuando el usuario cambie
  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  return {
    projects,
    loading,
    creating,
    deleting,
    createProject,
    deleteProject,
    getProject,
    refetch: loadProjects,
  };
};

export default useProjects;
